import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import { AutocompleteCDS, CheckboxCDS, TextFieldCDS } from '@sharedCR/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';
import { getConditionalArray } from 'utils/data';

import { closeConfirmationDialogAction, openConfirmationDialogAction } from 'store/global/actions';
import {
  editLoanLodeStarQuestionsAction,
  getLoanLodeStarQuestionsAction,
  resetLoanLodeStarQuestionsAction,
  showMessageThatCurrentUserCantChangeLoanAction,
} from 'store/loanApplications/actions';
import {
  selectIsCurrentUserCanChangeLoan,
  selectLoanLodeStarQuestionAnswers,
  selectLoanLodeStarQuestions,
} from 'store/loanApplications/selectors';

import {
  LODESTAR_QUESTION_INPUT_TYPE,
  LODESTAR_QUESTIONS_CONFIRMATION_ACCEPT_LABEL,
  LODESTAR_QUESTIONS_CONFIRMATION_CANCEL_LABEL,
  LODESTAR_QUESTIONS_CONFIRMATION_DESCRIPTION,
  LODESTAR_QUESTIONS_CONFIRMATION_MESSAGE,
  LODESTAR_QUESTIONS_CONFIRMATION_REJECT_LABEL,
  LODESTAR_QUESTIONS_DESCRIPTION,
  LODESTAR_QUESTIONS_RESET_BUTTON_LABEL,
  LODESTAR_QUESTIONS_SAVE_BUTTON_LABEL,
  LODESTAR_QUESTIONS_TITLE,
} from './LodeStarQuestions.constants';
import {
  actionsWrapperStyles,
  checkboxFieldWrapperStyles,
  fieldsWrapperStyles,
  fieldWrapperStyles,
  headerWrapperStyles,
  recalculateConfirmationActionsStyles,
  recalculateConfirmationBodyStyles,
  wrapperStyles,
} from './LodeStarQuestions.styles';
import { LodeStarQuestionsTestIds } from './LodeStarQuestions.testIds';
import {
  buildInitialFormFromAnswers,
  getAutocompleteOptions,
  getAutocompleteValue,
  getNumericInputProps,
  isFormDirty,
  isQuestionFieldChanged,
  serializeAnswersForSave,
} from './LodeStarQuestions.utils';

const RecalculateConfirmationBody = ({
  onCancel,
  onSaveAndRecalculate,
  onSaveWithoutRecalculate,
}) => (
  <Box sx={recalculateConfirmationBodyStyles}>
    <Typography component="h5" variant="bold16">
      {LODESTAR_QUESTIONS_CONFIRMATION_MESSAGE}
    </Typography>
    <Typography color="text.secondary" component="p" variant="regular14">
      {LODESTAR_QUESTIONS_CONFIRMATION_DESCRIPTION}
    </Typography>
    <Box sx={recalculateConfirmationActionsStyles}>
      <Button
        data-testid={LodeStarQuestionsTestIds.RECALCULATE_CANCEL_BUTTON}
        size="large"
        variant="line"
        onClick={onCancel}
      >
        {LODESTAR_QUESTIONS_CONFIRMATION_CANCEL_LABEL}
      </Button>
      <Button
        data-testid={LodeStarQuestionsTestIds.RECALCULATE_SKIP_BUTTON}
        size="large"
        variant="line"
        onClick={onSaveWithoutRecalculate}
      >
        {LODESTAR_QUESTIONS_CONFIRMATION_REJECT_LABEL}
      </Button>
      <Button
        data-testid={LodeStarQuestionsTestIds.RECALCULATE_CONFIRM_BUTTON}
        size="large"
        onClick={onSaveAndRecalculate}
      >
        {LODESTAR_QUESTIONS_CONFIRMATION_ACCEPT_LABEL}
      </Button>
    </Box>
  </Box>
);

RecalculateConfirmationBody.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSaveAndRecalculate: PropTypes.func.isRequired,
  onSaveWithoutRecalculate: PropTypes.func.isRequired,
};

const QuestionField = ({ question, formValue, onChange, isDisabled, isFieldChanged }) => {
  const fieldDataTestId = LodeStarQuestionsTestIds.getFieldId(question.identifier);

  const onCheckboxChange = useCallback(
    event => onChange(question.identifier, event.target.checked),
    [onChange, question.identifier],
  );

  const onSelectChange = useCallback(
    (event, option) => onChange(question.identifier, option ? option.value : ''),
    [onChange, question.identifier],
  );

  const onTextChange = useCallback(
    event => onChange(question.identifier, event.target.value),
    [onChange, question.identifier],
  );

  const numericProps = useMemo(() => getNumericInputProps({ question }), [question]);

  const selectOptions = useMemo(() => getAutocompleteOptions(question.options), [question.options]);

  const selectValue = useMemo(
    () => getAutocompleteValue({ options: question.options, formValue }),
    [question.options, formValue],
  );

  if (question.inputType === LODESTAR_QUESTION_INPUT_TYPE.CHECKBOX) {
    return (
      <Box sx={checkboxFieldWrapperStyles}>
        <CheckboxCDS
          dataTestId={fieldDataTestId}
          disabled={isDisabled}
          isFieldChanged={isFieldChanged}
          label={question.label}
          name={question.identifier}
          value={!!formValue}
          onChange={onCheckboxChange}
        />
      </Box>
    );
  }

  if (question.inputType === LODESTAR_QUESTION_INPUT_TYPE.SELECT) {
    return (
      <AutocompleteCDS
        dataTestId={fieldDataTestId}
        disabled={isDisabled}
        isFieldChanged={isFieldChanged}
        label={question.label}
        name={question.identifier}
        options={selectOptions}
        value={selectValue}
        onChange={onSelectChange}
      />
    );
  }

  return (
    <TextFieldCDS
      dataTestId={fieldDataTestId}
      disabled={isDisabled}
      inputProps={numericProps}
      isFieldChanged={isFieldChanged}
      label={question.label}
      name={question.identifier}
      value={formValue ?? ''}
      onChange={onTextChange}
    />
  );
};

QuestionField.propTypes = {
  question: PropTypes.shape({
    identifier: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    inputType: PropTypes.oneOf(Object.values(LODESTAR_QUESTION_INPUT_TYPE)).isRequired,
    valueType: PropTypes.string,
    defaultValue: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  formValue: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  isDisabled: PropTypes.bool,
  isFieldChanged: PropTypes.bool,
};

const LodeStarQuestions = ({ loanId }) => {
  const dispatch = useDispatch();

  const questions = useSelector(selectLoanLodeStarQuestions);
  const answers = useSelector(selectLoanLodeStarQuestionAnswers);
  const isCurrentUserCanChangeLoan = useSelector(selectIsCurrentUserCanChangeLoan);

  const [isSaving, setIsSaving] = useState(false);

  const initialFormValues = useMemo(
    () => buildInitialFormFromAnswers({ questions, answers }),
    [questions, answers],
  );

  const { form, initialForm, setForm, setInitialFormValues, setInForm } = useForm(
    initialFormValues,
    {},
  );

  useEffectSafeStrictMode(() => {
    if (loanId) {
      dispatch(getLoanLodeStarQuestionsAction({ loanId }));
    }

    return () => {
      dispatch(resetLoanLodeStarQuestionsAction());
    };
  }, [dispatch, loanId]);

  useEffect(() => {
    setInitialFormValues(initialFormValues);
    setForm(initialFormValues);
  }, [initialFormValues, setForm, setInitialFormValues]);

  const isDirty = useMemo(
    () => isFormDirty({ initialForm, currentForm: form }),
    [initialForm, form],
  );

  const isFieldsDisabled = !isCurrentUserCanChangeLoan || isSaving;

  const onFieldChange = useCallback(
    (identifier, value) => {
      setInForm(identifier, value);
    },
    [setInForm],
  );

  const onReset = useCallback(() => {
    setForm(initialForm);
  }, [initialForm, setForm]);

  const persistAnswers = useCallback(
    async ({ recalculateFees }) => {
      setIsSaving(true);

      try {
        const serializedAnswers = serializeAnswersForSave({ questions, form });

        await dispatch(
          editLoanLodeStarQuestionsAction({
            loanId,
            answers: serializedAnswers,
            recalculateFees,
          }),
        );
      } finally {
        setIsSaving(false);
      }
    },
    [dispatch, form, loanId, questions],
  );

  const closeDialog = useCallback(() => dispatch(closeConfirmationDialogAction()), [dispatch]);

  const onSaveAndRecalculate = useCallback(async () => {
    closeDialog();
    await persistAnswers({ recalculateFees: true });
  }, [closeDialog, persistAnswers]);

  const onSaveWithoutRecalculate = useCallback(async () => {
    closeDialog();
    await persistAnswers({ recalculateFees: false });
  }, [closeDialog, persistAnswers]);

  const confirmationBody = useMemo(
    () => (
      <RecalculateConfirmationBody
        onCancel={closeDialog}
        onSaveAndRecalculate={onSaveAndRecalculate}
        onSaveWithoutRecalculate={onSaveWithoutRecalculate}
      />
    ),
    [closeDialog, onSaveAndRecalculate, onSaveWithoutRecalculate],
  );

  const onSaveClick = useCallback(() => {
    if (!isCurrentUserCanChangeLoan) {
      dispatch(showMessageThatCurrentUserCantChangeLoanAction());

      return;
    }

    dispatch(
      openConfirmationDialogAction({
        message: LODESTAR_QUESTIONS_CONFIRMATION_MESSAGE,
        body: confirmationBody,
      }),
    );
  }, [confirmationBody, dispatch, isCurrentUserCanChangeLoan]);

  const fieldNodes = useMemo(
    () =>
      (questions || []).map(question => {
        const isFieldChanged = isQuestionFieldChanged({
          identifier: question.identifier,
          initialForm,
          currentForm: form,
        });

        return (
          <Box
            data-testid={LodeStarQuestionsTestIds.getFieldId(question.identifier)}
            key={question.identifier}
            sx={fieldWrapperStyles}
          >
            <QuestionField
              formValue={form?.[question.identifier]}
              isDisabled={isFieldsDisabled}
              isFieldChanged={isFieldChanged}
              question={question}
              onChange={onFieldChange}
            />
          </Box>
        );
      }),
    [form, onFieldChange, initialForm, isFieldsDisabled, questions],
  );

  if (!questions?.length) {
    return null;
  }

  const isSaveDisabled = !isDirty || isSaving || !isCurrentUserCanChangeLoan;
  const isResetDisabled = !isDirty || isSaving;

  const resetButton = (
    <Button
      data-testid={LodeStarQuestionsTestIds.RESET_BUTTON}
      disabled={isResetDisabled}
      key="reset"
      variant="line"
      onClick={onReset}
    >
      {LODESTAR_QUESTIONS_RESET_BUTTON_LABEL}
    </Button>
  );

  return (
    <Box data-testid={LodeStarQuestionsTestIds.ROOT} sx={wrapperStyles}>
      <Box sx={headerWrapperStyles}>
        <Typography data-testid={LodeStarQuestionsTestIds.TITLE} variant="bold20">
          {LODESTAR_QUESTIONS_TITLE}
        </Typography>
        <Typography data-testid={LodeStarQuestionsTestIds.DESCRIPTION} variant="regular14">
          {LODESTAR_QUESTIONS_DESCRIPTION}
        </Typography>
      </Box>
      <Box data-testid={LodeStarQuestionsTestIds.FORM} sx={fieldsWrapperStyles}>
        {fieldNodes}
      </Box>
      <Box sx={actionsWrapperStyles}>
        {getConditionalArray(isDirty, [resetButton])}
        <Button
          data-testid={LodeStarQuestionsTestIds.SAVE_BUTTON}
          disabled={isSaveDisabled}
          onClick={onSaveClick}
        >
          {LODESTAR_QUESTIONS_SAVE_BUTTON_LABEL}
        </Button>
      </Box>
    </Box>
  );
};

LodeStarQuestions.propTypes = {
  loanId: PropTypes.string.isRequired,
};

export default LodeStarQuestions;
